/**
 * Workstream D — Synthetic pretrain (M-1a + M-1b + M-4a + M-1d) admin page.
 *
 * Operator UX:
 *   1. Pick a known source (e.g. "CMS DE-SynPUF Sample 1") OR paste a URL OR
 *      upload a CSV/ZIP.
 *   2. Click "Start ingest" — backend creates a job + enqueues Celery.
 *   3. Watch staged progress: downloading → loading → pretrain_denial →
 *      pretrain_braided → complete.
 *   4. When complete, the page shows the registered model IDs for the
 *      synthetic prior and a "Train denial classifier now" button that
 *      kicks off the real-data retrain (warm-starting from the prior).
 *
 * Mirrors the IntelligenceImportManager / CBOImportManager pattern:
 * polling-based progress, no SSE.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import {
  Loader2,
  CheckCircle,
  AlertTriangle,
  Clock,
  Database,
  Upload,
  Play,
  RefreshCw,
  Link as LinkIcon,
  Brain,
  XCircle,
  Download,
  ArrowLeft,
  Sparkles,
} from 'lucide-react';

interface KnownSource {
  key: string;
  label: string;
  source: 'synpuf' | 'taf' | 'synthea';
  url: string;
  size_estimate_mb: number;
  row_estimate: number;
  notes: string;
}

interface Job {
  id: number;
  source: string;
  source_release: string | null;
  source_url: string | null;
  upload_id: number | null;
  source_path: string | null;
  file_size_bytes: number | null;
  job_type: string | null;
  status: string;
  current_stage: string | null;
  progress_percentage: number | null;
  progress_detail: any;
  max_rows: number | null;
  run_pretrain: boolean | null;
  members_created: number | null;
  claims_created: number | null;
  lines_created: number | null;
  denial_labels_created: number | null;
  activities_created: number | null;
  pretrain_denial_model_id: number | null;
  pretrain_braided_model_id: number | null;
  celery_task_id: string | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
}

interface Summary {
  counts: {
    synth_members?: number;
    synth_claims?: number;
    synth_lines?: number;
    synth_denial_labels?: number;
    synth_activities?: number;
  };
  priors: Array<{
    id: number;
    module: string;
    version: string;
    training_rows: number | null;
    trained_at: string;
    active: boolean;
    data_source: string;
    metrics: any;
  }>;
  latest_drift: { id: number; started_at: string; drift_metrics: any } | null;
}

function authHeaders(extra: Record<string, string> = {}) {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
    ...extra,
  };
}

const STAGE_ORDER = [
  'queued',
  'downloading',
  'downloaded',
  'extracted',
  'loading',
  'pretrain_denial',
  'pretrain_braided',
  'drift_check',
  'complete',
];

function stageRank(stage: string | null): number {
  if (!stage) return 0;
  const i = STAGE_ORDER.indexOf(stage);
  return i < 0 ? 0 : i;
}

export default function SyntheticPretrainPage() {
  const [knownSources, setKnownSources] = useState<KnownSource[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pretrainBusy, setPretrainBusy] = useState(false);
  const [pretrainInfo, setPretrainInfo] = useState<string | null>(null);

  // Form state
  const [mode, setMode] = useState<'url' | 'upload'>('url');
  const [selectedKey, setSelectedKey] = useState<string>('synpuf_sample_1');
  const [customUrl, setCustomUrl] = useState<string>('');
  const [source, setSource] = useState<'synpuf' | 'taf' | 'synthea'>('synpuf');
  const [maxRows, setMaxRows] = useState<number>(3_000_000);
  const [runPretrain, setRunPretrain] = useState(true);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedKnown = useMemo(
    () => knownSources.find((s) => s.key === selectedKey),
    [knownSources, selectedKey],
  );

  // ── data loading ─────────────────────────────────────────────
  async function loadAll() {
    // Skip the poll when offline — the page sets up a 5s interval, and
    // each tick while offline would otherwise spam ERR_INTERNET_DISCONNECTED
    // into the error banner and the console.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    try {
      const [ks, j, sm] = await Promise.all([
        fetch(getWeaveinApiUrl('/api/admin/synthetic-pretrain/known-sources'), {
          credentials: 'include',
          headers: authHeaders(),
        }),
        fetch(getWeaveinApiUrl('/api/admin/synthetic-pretrain/jobs?limit=20'), {
          credentials: 'include',
          headers: authHeaders(),
        }),
        fetch(getWeaveinApiUrl('/api/admin/synthetic-pretrain/summary'), {
          credentials: 'include',
          headers: authHeaders(),
        }),
      ]);
      if (ks.ok) setKnownSources(await ks.json());
      if (j.ok) setJobs(await j.json());
      if (sm.ok) setSummary(await sm.json());
    } catch (e: any) {
      // Transient network errors during a 5s poll are expected on flaky
      // connections; the next tick will recover. Only surface errors when
      // we're actually online (i.e. it's a real server-side problem).
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      setError(e.message || String(e));
    }
  }

  useEffect(() => {
    void loadAll();
    const t = setInterval(loadAll, 5000);
    // Resume polling immediately when the browser regains connectivity,
    // and clear any stale error banner from the offline period.
    const onOnline = () => {
      setError(null);
      void loadAll();
    };
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(t);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  // ── actions ─────────────────────────────────────────────────
  async function startUrlJob() {
    setError(null);
    setBusy(true);
    try {
      const url = selectedKnown?.url || customUrl;
      if (!url) throw new Error('Pick a known source or paste a URL');
      const created = await fetch(getWeaveinApiUrl('/api/admin/synthetic-pretrain/jobs'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          source: selectedKnown?.source || source,
          source_url: url,
          max_rows: maxRows,
          run_pretrain: runPretrain,
        }),
      });
      if (!created.ok) throw new Error(await created.text());
      const job: Job = await created.json();
      const started = await fetch(
        getWeaveinApiUrl(`/api/admin/synthetic-pretrain/jobs/${job.id}/start`),
        { method: 'POST', credentials: 'include', headers: authHeaders() },
      );
      if (!started.ok) throw new Error(await started.text());
      await loadAll();
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function startUploadJob() {
    setError(null);
    setBusy(true);
    try {
      if (!uploadFile) throw new Error('Pick a file');
      const fd = new FormData();
      fd.append('source', source);
      fd.append('file', uploadFile);
      const up = await fetch(getWeaveinApiUrl('/api/admin/synthetic-pretrain/upload'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: fd,
      });
      if (!up.ok) throw new Error(await up.text());
      const { id: uploadId } = await up.json();
      const created = await fetch(getWeaveinApiUrl('/api/admin/synthetic-pretrain/jobs'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          source,
          upload_id: uploadId,
          max_rows: maxRows,
          run_pretrain: runPretrain,
        }),
      });
      if (!created.ok) throw new Error(await created.text());
      const job: Job = await created.json();
      const started = await fetch(
        getWeaveinApiUrl(`/api/admin/synthetic-pretrain/jobs/${job.id}/start`),
        { method: 'POST', credentials: 'include', headers: authHeaders() },
      );
      if (!started.ok) throw new Error(await started.text());
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadAll();
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function cancelJob(id: number) {
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/admin/synthetic-pretrain/jobs/${id}/cancel`), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await res.text());
      await loadAll();
    } catch (e: any) {
      setError(e.message || String(e));
    }
  }

  async function deleteJob(id: number) {
    if (!confirm(`Permanently delete job ${id}? This removes the row from the database.`)) return;
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/admin/synthetic-pretrain/jobs/${id}`), {
        method: 'DELETE',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await res.text());
      await loadAll();
    } catch (e: any) {
      setError(e.message || String(e));
    }
  }

  async function retrainPriorsOnly() {
    if (!confirm(
      'Re-train denial classifier + braided ranker priors on the synthetic data ' +
      'already in the DB (no new ingest). Typical runtime: 2-5 minutes. Continue?',
    )) return;
    setError(null);
    setPretrainInfo(null);
    setPretrainBusy(true);
    try {
      const res = await fetch(
        getWeaveinApiUrl('/api/admin/synthetic-pretrain/pretrain-only'),
        { method: 'POST', credentials: 'include', headers: authHeaders() },
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPretrainInfo(
        `Queued. denial=${data.denial_task_id?.slice(0, 8)}, braided=${data.braided_task_id?.slice(0, 8)}. ` +
        `Watch /admin/billing-ml or the worker log for progress.`,
      );
      // Refresh summary in a moment so the new prior appears.
      setTimeout(loadAll, 3000);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setPretrainBusy(false);
    }
  }

  // ── helpers ─────────────────────────────────────────────────
  function fmt(d: string | null | undefined) {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString();
    } catch {
      return String(d);
    }
  }

  function n(x: number | null | undefined) {
    if (x == null) return '—';
    return Number(x).toLocaleString();
  }

  function statusPill(status: string, stage: string | null) {
    if (status === 'success' || stage === 'complete') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700">
          <CheckCircle className="w-3 h-3" /> complete
        </span>
      );
    }
    if (status === 'failed' || stage === 'failed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700">
          <XCircle className="w-3 h-3" /> failed
        </span>
      );
    }
    if (status === 'cancelled') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-zinc-100 text-zinc-700">
          <XCircle className="w-3 h-3" /> cancelled
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">
        <Loader2 className="w-3 h-3 animate-spin" /> {stage || status}
      </span>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Billing"
        title="Synthetic pretrain"
        description={
          <>
            Download CMS DE-SynPUF (or upload TAF / Synthea CSVs), ingest into the
            synthetic sentinel <code className="px-1 bg-paper-2 rounded">cbo_id = 0</code>{' '}
            namespace, then auto-train the denial classifier (M-1b) and braided ranker
            (M-4a) priors. Real-data retrains will warm-start from these priors.
          </>
        }
        actions={
          <>
            <Link
              to="/admin/billing-ml"
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Billing ML
            </Link>
            <button
              onClick={() => loadAll()}
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </>
        }
      />
      <PageContainer>

      <div className="px-4 py-3 rounded-md bg-indigo-50 border border-indigo-100 text-xs text-indigo-900 leading-relaxed">
        <span className="font-medium">How this fits in.</span> Synthetic priors live in their
        own model namespace (<code className="px-1 bg-white/60 rounded">is_synthetic_prior=TRUE</code>)
        and never serve inference. They exist so the weekly production training jobs on
        the <Link to="/admin/billing-ml" className="underline hover:text-indigo-700">Billing ML</Link> page
        can warm-start from a sensible initialization instead of random weights —
        useful while real ERAs are still accumulating. Run a full ingest if you want
        fresh data; use <em>Re-train priors</em> below if the data is already loaded
        and you just want refreshed model artifacts.
      </div>

      {error && (
        <div className="px-4 py-2 rounded-md bg-red-50 text-red-700 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      )}
      {pretrainInfo && (
        <div className="px-4 py-2 rounded-md bg-emerald-50 text-emerald-800 text-sm flex items-start gap-2">
          <CheckCircle className="w-4 h-4 mt-0.5" /> {pretrainInfo}
        </div>
      )}

      {/* ── summary tiles ───────────────────────────────────── */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryTile label="Synth members" value={summary?.counts?.synth_members} />
        <SummaryTile label="Synth claims" value={summary?.counts?.synth_claims} />
        <SummaryTile label="Synth lines" value={summary?.counts?.synth_lines} />
        <SummaryTile label="Synth denials" value={summary?.counts?.synth_denial_labels} />
        <SummaryTile label="Activities (codes)" value={summary?.counts?.synth_activities} />
      </section>

      {/* ── re-train priors only (no ingest) ─────────────────── */}
      <section className="border border-zinc-200 rounded-lg p-4 bg-surface flex items-center justify-between gap-4">
        <div>
          <h2 className="font-medium flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Re-train priors on existing data
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Refits the synthetic <code className="px-1 bg-zinc-100 rounded">denial_classifier</code> and{' '}
            <code className="px-1 bg-zinc-100 rounded">braided_ranker</code> priors on
            data already in the DB (skips download/ingest). Typical runtime 2–5 min.
            Use this after a feature-column change, or to pick up new synthetic rows
            without a full re-ingest.
          </p>
        </div>
        <button
          onClick={retrainPriorsOnly}
          disabled={pretrainBusy || ((summary?.counts?.synth_denial_labels ?? 0) < 5000)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
          title={
            (summary?.counts?.synth_denial_labels ?? 0) < 5000
              ? 'Need at least 5,000 synthetic denial labels — ingest first.'
              : 'Fire pretrain_denial_classifier_synthetic_task + pretrain_braided_ranker_synthetic_task'
          }
        >
          {pretrainBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Re-train priors
        </button>
      </section>

      {/* ── start new job ───────────────────────────────────── */}
      <section className="border border-zinc-200 rounded-lg p-4 bg-surface">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-medium flex items-center gap-2">
            <Database className="w-4 h-4" /> Start new ingest
          </h2>
          <div className="inline-flex rounded-md border border-zinc-300 text-sm">
            <button
              onClick={() => setMode('url')}
              className={`px-3 py-1 rounded-l-md ${mode === 'url' ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-50'}`}
            >
              <LinkIcon className="w-3.5 h-3.5 inline mr-1" />
              URL download
            </button>
            <button
              onClick={() => setMode('upload')}
              className={`px-3 py-1 rounded-r-md ${mode === 'upload' ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-50'}`}
            >
              <Upload className="w-3.5 h-3.5 inline mr-1" />
              File upload
            </button>
          </div>
        </div>

        {mode === 'url' ? (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Known sources</label>
              <select
                className="w-full border rounded-md px-2 py-1.5 text-sm"
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
              >
                <option value="">— Custom URL —</option>
                {knownSources.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label} (~{s.size_estimate_mb} MB, ~
                    {(s.row_estimate / 1_000_000).toFixed(1)}M rows)
                  </option>
                ))}
              </select>
              {selectedKnown && (
                <p className="text-xs text-zinc-500 mt-1">{selectedKnown.notes}</p>
              )}
            </div>
            {!selectedKnown && (
              <div>
                <label className="block text-sm font-medium mb-1">Custom URL</label>
                <input
                  type="url"
                  className="w-full border rounded-md px-2 py-1.5 text-sm font-mono"
                  placeholder="https://..."
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                />
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Source format</label>
                    <select
                      className="w-full border rounded-md px-2 py-1.5 text-sm"
                      value={source}
                      onChange={(e) => setSource(e.target.value as any)}
                    >
                      <option value="synpuf">synpuf (CMS DE-SynPUF carrier)</option>
                      <option value="taf">taf (Medicaid TAF OT)</option>
                      <option value="synthea">synthea (Synthea claims CSV)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Source format</label>
                <select
                  className="w-full border rounded-md px-2 py-1.5 text-sm"
                  value={source}
                  onChange={(e) => setSource(e.target.value as any)}
                >
                  <option value="synpuf">synpuf</option>
                  <option value="taf">taf</option>
                  <option value="synthea">synthea</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">CSV / ZIP / GZ file</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.gz,.zip,.csv.gz"
                  className="w-full text-sm"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Max 500 MB. For SynPUF Sample 1 (~280 MB zipped), use URL download instead — the
                  browser upload path is too slow for that file size.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-sm font-medium mb-1">Max rows</label>
            <input
              type="number"
              min={1000}
              step={100000}
              className="w-full border rounded-md px-2 py-1.5 text-sm"
              value={maxRows}
              onChange={(e) => setMaxRows(Number(e.target.value))}
            />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              id="run_pretrain"
              checked={runPretrain}
              onChange={(e) => setRunPretrain(e.target.checked)}
            />
            <label htmlFor="run_pretrain" className="text-sm">
              Auto-train denial classifier + braided ranker after ingest
            </label>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            disabled={busy}
            onClick={() => (mode === 'url' ? startUrlJob() : startUploadJob())}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Start ingest
          </button>
        </div>
      </section>

      {/* ── active priors ───────────────────────────────────── */}
      {summary && summary.priors.length > 0 && (
        <section className="border border-zinc-200 rounded-lg p-4 bg-surface">
          <h2 className="font-medium mb-3 flex items-center gap-2">
            <Brain className="w-4 h-4" /> Registered synthetic priors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {summary.priors.map((p) => (
              <div key={p.id} className="rounded-md border border-zinc-200 p-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">{p.module}</span>
                  <span className="text-xs text-zinc-500">v{p.version}</span>
                </div>
                <div className="text-xs text-zinc-600">
                  rows={n(p.training_rows)} · trained {fmt(p.trained_at)}{' '}
                  {p.active ? '· active' : ''}
                </div>
                {p.metrics?.auc != null && (
                  <div className="text-xs text-zinc-700">AUC {Number(p.metrics.auc).toFixed(3)}</div>
                )}
              </div>
            ))}
          </div>
          {summary.latest_drift && (
            <div className="mt-3 text-xs text-zinc-600">
              Latest drift check: max_kl ={' '}
              {summary.latest_drift.drift_metrics?.max_kl ?? '—'} at{' '}
              {fmt(summary.latest_drift.started_at)}
              {summary.latest_drift.drift_metrics?.breached && (
                <span className="ml-2 text-red-600">breached (synthetic prior auto-deactivated)</span>
              )}
            </div>
          )}
        </section>
      )}

      {/* ── job list ────────────────────────────────────────── */}
      <section className="border border-zinc-200 rounded-lg bg-surface">
        <h2 className="font-medium px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Recent jobs
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">ID</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Stage / Status</th>
                <th className="text-left px-3 py-2">Progress</th>
                <th className="text-left px-3 py-2">Loaded</th>
                <th className="text-left px-3 py-2">Priors</th>
                <th className="text-left px-3 py-2">Started</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-6 text-zinc-500">
                    No jobs yet — start one above.
                  </td>
                </tr>
              )}
              {jobs.map((j) => (
                <tr key={j.id} className="border-t border-zinc-100">
                  <td className="px-3 py-2">{j.id}</td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs">{j.source}</div>
                    {j.source_release && (
                      <div className="text-xs text-zinc-500">{j.source_release}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">{statusPill(j.status, j.current_stage)}</td>
                  <td className="px-3 py-2 w-64">
                    <div className="w-full bg-zinc-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-zinc-800 h-1.5"
                        style={{ width: `${Math.min(100, Math.max(0, Number(j.progress_percentage || 0)))}%` }}
                      />
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {Number(j.progress_percentage || 0).toFixed(0)}% · step{' '}
                      {stageRank(j.current_stage)}/{STAGE_ORDER.length - 1}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>lines {n(j.lines_created)}</div>
                    <div className="text-zinc-500">
                      denials {n(j.denial_labels_created)} · members {n(j.members_created)}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {j.pretrain_denial_model_id ? `D#${j.pretrain_denial_model_id}` : '—'}
                    {' / '}
                    {j.pretrain_braided_model_id ? `B#${j.pretrain_braided_model_id}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">{fmt(j.started_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {['queued', 'running'].includes(j.status) && (
                        <button
                          onClick={() => cancelJob(j.id)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Cancel
                        </button>
                      )}
                      {j.status !== 'running' && (
                        <button
                          onClick={() => deleteJob(j.id)}
                          className="text-xs text-zinc-500 hover:text-red-700 hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </PageContainer>
    </div>
  );
}

function SummaryTile({
  label,
  value,
}: {
  label: string;
  value: number | null | undefined;
}) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-surface p-3">
      <div className="text-xs text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold mt-0.5">
        {value == null ? '—' : Number(value).toLocaleString()}
      </div>
    </div>
  );
}
